import { toast } from "@fox1ck-org/ui/toast";
import type { ReportView } from "@gen/tracker/v1/reporting_pb";

import { errorText } from "@/lib/errors";
import { useT } from "@/lib/i18n";
import { useCreateReportView, useDeleteReportView } from "@/lib/services/report-view-service";

/**
 * × on a saved view's chip (and «Удалить» in the saved-reports list): the
 * view goes at once and the toast says so with «Отменить», which saves it
 * back — the same name, the same definition, the same reach — and opens it
 * again. One click must not cost a view somebody built by hand.
 *
 * The restored view is a new row (a new id, the restorer as its author); a
 * per-user default that pointed at the old one is not carried over.
 */
export function useDeleteViewWithUndo() {
  const t = useT();
  const remove = useDeleteReportView();
  const restore = useCreateReportView();
  return (
    view: ReportView,
    { deleted, onDeleted, onRestored }: { deleted: string; onDeleted?: () => void; onRestored?: (view: ReportView) => void },
  ) =>
    remove.mutate(view.id, {
      onSuccess: () => {
        onDeleted?.();
        toast.success(deleted, {
          // Long enough to read the name and reach «Отменить».
          duration: 8000,
          action: view.definition
            ? {
                label: t("common.undo"),
                onClick: () =>
                  restore.mutate(
                    { name: view.name, definition: view.definition!, workspaceShared: view.scope === "workspace" },
                    {
                      onSuccess: (back) => back && onRestored?.(back),
                      onError: (e) => toast.error(errorText(e) || t("common.undoFailed")),
                    },
                  ),
              }
            : undefined,
        });
      },
      onError: (e) => toast.error(errorText(e) || t("common.deleteFailed")),
    });
}
